/**
 * ClickAir ATC — intercepts native product forms so the cart drawer opens
 * instead of navigating to /cart.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';
import { morph } from '@theme/morph';

const FORM_SELECTOR = 'form.ca-pf__cta-form';

/**
 * @param {SubmitEvent} event
 */
async function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.matches(FORM_SELECTOR)) return;

  // If drawer isn't on the page, allow normal navigation to /cart
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer) return;

  event.preventDefault();
  event.stopPropagation();

  const button = form.querySelector('button[type="submit"]');
  if (button instanceof HTMLButtonElement) button.disabled = true;

  const formData = new FormData(form);
  const sectionIds = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionIds.push(el.dataset.sectionId);
    }
  });
  if (!sectionIds.includes('cart-drawer-section')) {
    sectionIds.push('cart-drawer-section');
  }
  formData.set('sections', sectionIds.join(','));
  formData.set('sections_url', window.location.pathname);

  const qty = Number(formData.get('quantity')) || 1;
  const merchandiseId = String(formData.get('id') || '');
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId, quantity: qty }],
      promise: deferred.promise,
    })
  );

  try {
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });
    const data = await response.json();

    if (data.status) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(await fetchCartJson()),
        detail: { didError: true, source: 'clickair-add-to-cart' },
      });
      return;
    }

    if (data.sections) {
      Object.entries(data.sections).forEach(([id, html]) => {
        if (typeof html !== 'string') return;
        const current = document.getElementById(`shopify-section-${id}`);
        if (!current) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.getElementById(`shopify-section-${id}`) || doc.body.firstElementChild;
        if (next) morph(current, next);
      });
    }

    const cart = await fetchCartJson();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, source: 'clickair-add-to-cart', items: cart.items },
    });

    // Ensure drawer opens even if auto-open setting is off / event timing races
    /** @type {HTMLElement & { open?: () => void } | null} */
    const themeDrawer = document.querySelector('theme-drawer#cart-drawer');
    themeDrawer?.open?.();
  } catch (error) {
    deferred.reject(error);
    console.error('[ClickAir] add to cart failed', error);
  } finally {
    if (button instanceof HTMLButtonElement) button.disabled = false;
  }
}

async function fetchCartJson() {
  const res = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
  return res.json();
}

document.addEventListener('submit', onSubmit, true);
